import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { guestDisplayName, type Guest } from '@peles/shared';
import { useEventGuests } from '../../../hooks/useEventGuests';

export interface GuestSearchProps {
  eventId: string;
  /** Guests not to offer (the guest being edited, those already chosen). */
  excludeIds: string[];
  onChoose: (guest: Guest) => void;
  placeholder: string;
  ariaLabel: string;
}

/** Find another guest of the event by name, email or phone — searched on the server. */
export function GuestSearch({
  eventId,
  excludeIds,
  onChoose,
  placeholder,
  ariaLabel,
}: GuestSearchProps) {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQ(text.trim()), 250);
    return () => clearTimeout(timer);
  }, [text]);
  const { data, isFetching } = useEventGuests(q ? eventId : undefined, { q, pageSize: 8 });
  const results = (data?.data ?? []).filter((g) => !excludeIds.includes(g.id));

  return (
    <div className="relative">
      <Search
        size={14}
        className="absolute left-3 top-3 text-gray-400 pointer-events-none"
        aria-hidden
      />
      <input
        type="search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
      />
      {q && (
        <ul
          role="listbox"
          aria-label={`${ariaLabel} results`}
          className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          {isFetching && results.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">Searching…</li>
          )}
          {!isFetching && results.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">No guest matches</li>
          )}
          {results.map((guest) => (
            <li key={guest.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => {
                  onChoose(guest);
                  setText('');
                  setQ('');
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              >
                {guestDisplayName(guest)}
                {guest.familyGroup && (
                  <span className="ml-1 text-xs text-gray-500">· {guest.familyGroup}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
